/**
 * MIT License
 * Copyright (c) 2024 Theo Gravity
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

/**
 * This file was copied verbatim from datadog-transport-common
 * npm package to avoid including @datadog/datadog-api-client which is
 * a large package.
 * https://github.com/theogravity/datadog-transports/blob/main/packages/datadog-transport-common/src/LogStorage.ts
 */
import type { HTTPLogItem } from "@datadog/datadog-api-client/dist/packages/datadog-api-client-v2/models/HTTPLogItem";

function generateCurrentPointer() {
	return `${Date.now()}_${Math.floor(Math.random() * 1000)}`;
}

interface LogBucketData {
	/**
	 * Size of the items in bytes
	 */
	size: number;
	logItems: Array<HTTPLogItem>;
}

/**
 * Manages storage of log data into buckets.
 */
export class LogStorage {
	/**
	 * Identifier for the current log bucket.
	 * @type {string}
	 */
	// @ts-ignore
	currentBucket: string;

	/**
	 * Stores the log data buckets by their identifiers.
	 * @type {Record<string, LogBucketData>}
	 * @private
	 */
	private logBucket: Record<string, LogBucketData>;

	/**
	 * Initializes the log storage and creates the first log bucket.
	 */
	constructor() {
		this.logBucket = {};
		this.newLogBucket();
	}

	/**
	 * Creates a new log bucket, deleting the current one if it exists.
	 * @private
	 */
	private newLogBucket() {
		if (this.currentBucket) {
			delete this.logBucket[this.currentBucket];
		}

		this.currentBucket = generateCurrentPointer();
		this.logBucket[this.currentBucket] = {
			size: 0,
			logItems: [],
		};
	}

	/**
	 * Retrieves the byte size of the current log bucket.
	 * @returns {number} The size of the current log bucket in bytes.
	 */
	getLogBucketByteSize(): number {
		return this.logBucket[this.currentBucket]?.size || 0;
	}

	/**
	 * Gets the count of logs in the current log bucket.
	 * @returns {number} The number of log items in the current bucket.
	 */
	getLogCount(): number {
		return this.logBucket[this.currentBucket]?.logItems.length || 0;
	}

	/**
	 * Finishes the current log batch, returning its logs and creating a new log bucket.
	 * @returns {Array<HTTPLogItem>} The log items from the finished log bucket.
	 */
	finishLogBatch(): Array<HTTPLogItem> {
		const logs = this.logBucket[this.currentBucket]?.logItems;

		this.newLogBucket();

		return logs || [];
	}

	/**
	 * Adds a log item to the current log bucket.
	 * @param {HTTPLogItem} log - The log item to be added.
	 * @param {number} logByteSize - The size of the log item in bytes.
	 */
	addLog(log: HTTPLogItem, logByteSize: number) {
		const data = this.logBucket[this.currentBucket];
		if (!data) {
			return;
		}

		data.size += logByteSize;
		data.logItems.push(log);
	}
}
